import { BusinessError, EntityKind, ErrorKind } from '../errors';
import { SollicitationsSchema, isResultError, isResultSuccess, SollicitationsTypes, DemandesFinancementStatus, isDemandeFinancementStatus, } from '../types';
/**
 * Sollicitations service
 */
class SollicitationsService {
    sollicitationsAdapter;
    demandesFinancementService;
    contributionsService;
    storeService;
    sollicitationsFormatter;
    constructor({ sollicitationsAdapter, demandesFinancementService, contributionsService, storeService, sollicitationsFormatter, }) {
        this.sollicitationsAdapter = sollicitationsAdapter;
        this.demandesFinancementService = demandesFinancementService;
        this.contributionsService = contributionsService;
        this.storeService = storeService;
        this.sollicitationsFormatter = sollicitationsFormatter;
    }
    /**
     * Get formatted awaiting total
     */
    getFormattedAwaitingTotal(awaitingTotal) {
        // When the total is above 9, we display 9+
        const total = typeof awaitingTotal === 'string' ? Number.parseInt(awaitingTotal) : awaitingTotal;
        return total >= 10 ? '9+' : awaitingTotal;
    }
    /**
     * Format awaiting total
     */
    formatAwaitingTotal(total) {
        if (!total)
            return;
        total.awaiting = this.getFormattedAwaitingTotal(total.awaiting);
    }
    /**
     * Count
     */
    count() {
        return this.sollicitationsAdapter.count();
    }
    /**
     * Count total
     */
    async countTotal() {
        let total = 0;
        const res = await this.count();
        const sollicitationsCount = isResultSuccess(res) && res.response?.count;
        if (isResultError(res) || !sollicitationsCount) {
            return {
                error: new BusinessError(ErrorKind.TECHNICAL_ERROR, {
                    entity: EntityKind.SOLLICITATIONS,
                }),
            };
        }
        for (const key of Object.keys(sollicitationsCount)) {
            total += sollicitationsCount[key].awaiting;
        }
        return { response: this.getFormattedAwaitingTotal(total) };
    }
    /**
     * Get sollicitations menu
     */
    async getSollicitationsMenu() {
        const res = await this.count();
        const sollicitationsCount = isResultSuccess(res) && res.response?.count;
        const tabs = [];
        if (isResultError(res) || !sollicitationsCount) {
            return {
                error: new BusinessError(ErrorKind.TECHNICAL_ERROR, {
                    entity: EntityKind.SOLLICITATIONS,
                }),
            };
        }
        for (const key of Object.keys(sollicitationsCount)) {
            this.formatAwaitingTotal(sollicitationsCount[key]);
            const tab = {
                type: key,
                label: key,
                badge: {
                    value: sollicitationsCount[key].awaiting,
                    label: key,
                },
                visible: sollicitationsCount[key].awaiting != 0 || sollicitationsCount[key].completed != 0,
            };
            if (sollicitationsCount[key].awaiting == 0)
                delete tab.badge;
            tabs.push(tab);
        }
        return { response: tabs };
    }
    /**
     * Get sollicitations by type
     */
    async getSollicitationsByType(sollicitationsType, pagination, status, filters) {
        try {
            const currentUser = this.storeService.getCurrentUser().data();
            if (!currentUser) {
                throw new Error('publicSettings is not loaded in the store');
            }
            const sollicitations = await this.sollicitationsAdapter.getSollicitationsByType(sollicitationsType, pagination, status, filters);
            if (isResultError(sollicitations)) {
                throw sollicitations.error;
            }
            const sollicitationsResponse = SollicitationsSchema.parse(sollicitations.response);
            // Returns formatted sollicitations to avoid business code in front
            let formattedSollicitations = {
                ...sollicitationsResponse,
                awaitingActions: [],
                completedActions: [],
            };
            switch (sollicitationsResponse.type) {
                case SollicitationsTypes.COMPLEMENTS: {
                    const formattedComplements = sollicitationsResponse.awaitingActions.map((action) => {
                        return this.sollicitationsFormatter.formatComplements(action);
                    });
                    formattedSollicitations = {
                        ...sollicitationsResponse,
                        awaitingActions: formattedComplements,
                        completedActions: [],
                    };
                    break;
                }
                case SollicitationsTypes.PIECES: {
                    const formattedDcp = sollicitationsResponse.awaitingActions.map((action) => this.sollicitationsFormatter.formatDcp(action));
                    formattedSollicitations = {
                        ...sollicitationsResponse,
                        awaitingActions: formattedDcp,
                        completedActions: [],
                    };
                    break;
                }
                case SollicitationsTypes.ATTESTATIONS: {
                    const formattedAttestations = sollicitationsResponse.awaitingActions.map((action) => this.sollicitationsFormatter.formatAttestations(action));
                    formattedSollicitations = {
                        ...sollicitationsResponse,
                        awaitingActions: formattedAttestations,
                        completedActions: [],
                    };
                    break;
                }
                case SollicitationsTypes.JUSTIFICATIONS: {
                    const formattedJustifications = [];
                    await Promise.all(sollicitationsResponse.awaitingActions.map(async (action) => {
                        const awaitingAction = this.sollicitationsFormatter.formatJustifications(action, currentUser);
                        formattedJustifications.push(awaitingAction);
                    }));
                    formattedSollicitations = {
                        ...sollicitationsResponse,
                        awaitingActions: formattedJustifications,
                        completedActions: [],
                    };
                    break;
                }
                case SollicitationsTypes.AVIS: {
                    const formattedAwaitingAvis = sollicitationsResponse.awaitingActions.map((action) => this.sollicitationsFormatter.formatAvisAwaiting(action));
                    const formattedCompletedAvis = sollicitationsResponse.completedActions.map((action) => this.sollicitationsFormatter.formatAvisCompleted(action));
                    formattedSollicitations = {
                        ...sollicitationsResponse,
                        awaitingActions: formattedAwaitingAvis,
                        completedActions: formattedCompletedAvis,
                    };
                    break;
                }
                case SollicitationsTypes.AVIS_PREALABLES: {
                    const formattedAwaitingAvis = sollicitationsResponse.awaitingActions.map((action) => this.sollicitationsFormatter.formatAvisPrealablesAwaiting(action));
                    const formattedCompletedAvis = sollicitationsResponse.completedActions.map((action) => this.sollicitationsFormatter.formatAvisPrealablesCompleted(action));
                    formattedSollicitations = {
                        ...sollicitationsResponse,
                        awaitingActions: formattedAwaitingAvis,
                        completedActions: formattedCompletedAvis,
                    };
                    break;
                }
            }
            return {
                response: formattedSollicitations,
            };
        }
        catch (error) {
            console.error(error);
            return {
                error: new BusinessError(ErrorKind.TECHNICAL_ERROR, {
                    entity: EntityKind.SOLLICITATIONS,
                }),
            };
        }
    }
    /**
     * Attest demandes de financement
     */
    async attestDemandes(demandes) {
        const contributionsPromises = demandes.map(({ id }) => this.contributionsService.getWaitingForCertificateContributionFromDemande(id));
        const contributionsResponse = await Promise.all(contributionsPromises);
        const contributions = contributionsResponse
            // eslint-disable-next-line no-underscore-dangle
            .map((contrib) => (isResultSuccess(contrib) && contrib.response._embedded.items[0]) || null)
            .filter((contrib) => contrib !== null);
        const updateDemandes = demandes.map(async (demande) => {
            const contribution = contributions.find((contribution) => {
                return contribution?.demande.href === demande.id;
            });
            if (!contribution) {
                const result = await this.demandesFinancementService.transmitDemandeFinancement(demande.reference);
                if (isResultSuccess(result)) {
                    return result.response;
                }
                throw result.error;
            }
            const demandeFinancementWithContribution = await this.demandesFinancementService.getDemandeFinancement(demande.reference, contribution.reference);
            demandeFinancementWithContribution.status = DemandesFinancementStatus.supported;
            // Should get last status before it was waiting for certificate.
            // This is in case we have a contribution after recevabilite or instruction en cours
            const statusEvents = demandeFinancementWithContribution.history.events?.filter((event) => event.type === 'STATUS') ?? [];
            const indexLastWaitingForCertificate = statusEvents.findLastIndex((event) => event.reference === DemandesFinancementStatus.waitingForCertificate);
            const oldStatus = indexLastWaitingForCertificate > 0 && statusEvents[indexLastWaitingForCertificate - 1]?.reference;
            if (oldStatus && isDemandeFinancementStatus(oldStatus)) {
                demandeFinancementWithContribution.status = oldStatus;
            }
            await this.demandesFinancementService.updateDemandeFinancement(demandeFinancementWithContribution);
            await this.contributionsService.answerContribution(contribution.reference);
            return demandeFinancementWithContribution;
        });
        const results = await Promise.all(updateDemandes);
        const response = results.filter((e) => e !== undefined);
        return { response };
    }
}
export { SollicitationsService };
//# sourceMappingURL=sollicitations-service.js.map